import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Table, Card, Tag, Space, Button, message, Popconfirm } from 'antd'
import { EyeOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { getAccounts, updateAccountStatus, type Account } from '../api/admin'

const Accounts = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['accounts', page, pageSize],
    queryFn: () => getAccounts(page, pageSize).then((res) => res.data.data),
  })

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await updateAccountStatus(id, status)
      message.success('状态更新成功')
      refetch()
    } catch (error) {
      message.error('状态更新失败')
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          active: 'green',
          pending: 'orange',
          suspended: 'red',
        }
        const textMap: Record<string, string> = {
          active: '活跃',
          pending: '待验证',
          suspended: '已禁用',
        }
        return <Tag color={colorMap[status]}>{textMap[status]}</Tag>
      },
    },
    {
      title: '邮箱验证',
      dataIndex: 'email_verified',
      render: (verified: boolean) => (
        <Tag color={verified ? 'green' : 'red'}>
          {verified ? '已验证' : '未验证'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Account) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/accounts/${record.id}`)}
          >
            查看
          </Button>
          {record.status === 'active' ? (
            <Popconfirm
              title="确认禁用"
              description="确定要禁用该账户吗？"
              onConfirm={() => handleStatusChange(record.id, 'suspended')}
            >
              <Button type="link" danger icon={<StopOutlined />}>
                禁用
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusChange(record.id, 'active')}
            >
              启用
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>账户管理</h2>
      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize,
            total: data?.total || 0,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps || 20)
            },
          }}
        />
      </Card>
    </div>
  )
}

export default Accounts
